import { type EChartsExtensionInstallRegisters } from 'echarts/lib/extension';
import type { Payload } from 'echarts/lib/echarts.all';
import type GlobalModel from 'echarts/lib/model/Global';
import Tree from 'echarts/lib/data/Tree';
import {
  type RelationshipDiagramSeriesModelClazz as RelationshipDiagramSeriesModel,
  type RelationshipDiagramSeriesNodeItemOption,
  type RelationTreeNode
} from './RelationshipDiagramSeries';

type CollapsedInfo = {
  nodeData?: RelationshipDiagramSeriesNodeItemOption;
  collapsed?: boolean;
};

export type RelationshipDiagramActionPayload = Payload & {
  dataIndexAndTreeIndex?: Map<string, number[]>;
  seriesIndex?: number;
  seriesId?: number;
  seriesName?: string;
  clickType?: string;
  nodeCollapsedInfo?: CollapsedInfo[];
  key?: string | number;
  value?: string | boolean;
  dataIndex?: number;
};

export function installRelationshipDiagramAction(registers: EChartsExtensionInstallRegisters) {
  // 主节点的展开收起
  registers.registerAction(
    { type: 'dvRelationshipDiagramExpandAndCollapse', update: 'update' },
    (payload: RelationshipDiagramActionPayload, ecModel: GlobalModel) => {
      const result = ecModel.findComponents({
        mainType: 'series',
        subType: 'dvRelationshipDiagram',
        query: {
          seriesIndex: payload.seriesIndex,
          seriesId: payload.seriesId,
          seriesName: payload.seriesName
        }
      }) as RelationshipDiagramSeriesModel[];
      result.forEach((seriesModel: RelationshipDiagramSeriesModel) => {
        const nodeCollapsedInfo: CollapsedInfo[] = [];
        payload.dataIndexAndTreeIndex.forEach((value, key) => {
          const tree = seriesModel.trees[key] as Tree;
          value.forEach(dataIndex => {
            const node = tree.getNodeByDataIndex(dataIndex);
            node.isExpand = !node.isExpand;
            node.children.forEach(childNode => {
              childNode.isExpand = node.isExpand;
              nodeCollapsedInfo.push({
                nodeData: tree.data.getRawDataItem(
                  childNode.dataIndex
                ) as RelationshipDiagramSeriesNodeItemOption,
                collapsed: !childNode.isExpand
              });
            });
          });
        });
        payload.nodeCollapsedInfo = nodeCollapsedInfo;
      });
    }
  );

  // 单节点的状态改变
  registers.registerAction(
    { type: 'dvRelationshipDiagramChangeState', update: 'update' },
    (payload: RelationshipDiagramActionPayload, ecModel: GlobalModel) => {
      const result = ecModel.findComponents({
        mainType: 'series',
        subType: 'dvRelationshipDiagram',
        query: {
          seriesIndex: payload.seriesIndex,
          seriesId: payload.seriesId,
          seriesName: payload.seriesName
        }
      }) as RelationshipDiagramSeriesModel[];
      result.forEach((seriesModel: RelationshipDiagramSeriesModel) => {
        const node = seriesModel.trees[0].getNodeByDataIndex(payload.dataIndex);
        node[payload.key] = payload.value;
      });
    }
  );

  // 全部节点的状态改变
  registers.registerAction(
    { type: 'dvRelationshipDiagramChangeAllState', update: 'update' },
    (payload: RelationshipDiagramActionPayload, ecModel: GlobalModel) => {
      const result = ecModel.findComponents({
        mainType: 'series',
        subType: 'dvRelationshipDiagram',
        query: {
          seriesIndex: payload.seriesIndex,
          seriesId: payload.seriesId,
          seriesName: payload.seriesName
        }
      }) as RelationshipDiagramSeriesModel[];
      result.forEach((seriesModel: RelationshipDiagramSeriesModel) => {
        const treeFakeVirtualRoot = seriesModel.trees[0].root as unknown as RelationTreeNode;

        const treeFakeRealRoot = treeFakeVirtualRoot.children[0] as RelationTreeNode;
        treeFakeRealRoot.eachNode((node: RelationTreeNode) => {
          if (Object.keys(node).includes(payload.key as string)) {
            node[payload.key] = !node[payload.key];
          }
        });
      });
    }
  );
}
